import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import {
  Dialog,
  DialogContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
} from "@material-ui/core";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { X as LucideX, CheckSquare, Square } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { useCurrencyContext } from "../../context/Currency/CurrencyContext";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CancelButton } from "../ActionButtons";
import CustomToggleSwitch from "../CustomToggleSwitch";
import useFilterDropdownStyles from "../SharedStyles/useFilterDropdownStyles";

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    dialogPaper: {
      ...(ds.dialogPaper || {}),
      background: isDark ? "#121212" : "#ffffff",
    },
    header: {
      ...(ds.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(ds.headerTitle || {}),
    },
    closeButton: {
      backgroundColor:
        ds.closeButton?.backgroundColor || "rgba(255, 255, 255, 0.08)",
      color: ds.closeButton?.color || "#ffffff",
      transition: ds.closeButton?.transition || "all 0.2s ease",
    },
    content: {
      padding: "20px 20px 16px",
      ...(theme.scrollbarStyles || {}),
    },
    field: {
      width: "100%",
    },
    sectionRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      marginTop: 4,
      marginBottom: 4,
    },
    sectionLabel: {
      fontSize: 12,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      color: isDark ? "#8696a0" : "#667781",
    },
    bulkActions: {
      display: "inline-flex",
      gap: 6,
    },
    bulkBtn: {
      borderRadius: 10,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 12,
      padding: "6px 12px",
      gap: 6,
      border: "none",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      color: isDark ? "#e9edef" : "#0f172a",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      transition: "background 0.18s ease",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
      },
    },
    toggleItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(255,255,255,0.05)"
          : "#f5f5f5",
        borderColor: isDark
          ? "rgba(255,255,255,0.20)"
          : "#bdbdbd",
      },
    },
    toggleLabel: {
      fontSize: "0.9rem",
      color: isDark ? "#e9edef" : "#424242",
      fontWeight: 500,
    },
    deleteBtn: {
      borderRadius: 12,
      textTransform: "none",
      fontWeight: 500,
      fontSize: 13,
      padding: "0 18px",
      height: 38,
      border: "none",
      cursor: "pointer",
      color: "#fff",
      background: "#e53935",
      transition: "background 0.18s ease",
      "&:hover": { background: "#c62828" },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    scrollableMenuPaper: {
      maxHeight: 240,
      overflowY: "auto",
      ...(theme.scrollbarStyles || {}),
      "& .MuiAutocomplete-listbox": {
        maxHeight: 220,
        ...(theme.scrollbarStyles || {}),
      },
    },
  };
});

const currencyFilterOptions = createFilterOptions({
  stringify: (opt) => `${opt.code} ${opt.symbol} ${opt.name || ""}`,
});

const FEATURE_TOGGLES = [
  { name: "useWhatsapp", labelKey: "settings.managers.plans.whatsapp" },
  { name: "useFacebook", labelKey: "settings.managers.plans.facebook" },
  { name: "useInstagram", labelKey: "settings.managers.plans.instagram" },
  { name: "useCampaigns", labelKey: "plans.form.campaigns" },
  { name: "useSchedules", labelKey: "plans.form.schedules" },
  { name: "useExternalApi", labelKey: "settings.managers.plans.externalAPI" },
  { name: "useKanban", labelKey: "settings.managers.plans.kanban" },
  { name: "useOfficialAPI", labelKey: "settings.managers.plans.officialAPI" },
  { name: "useIntegrations", labelKey: "settings.managers.plans.integrations" },
];

const buildEmpty = (defaultCurrency) => ({
  name: "",
  users: 0,
  connections: 0,
  queues: 0,
  amount: 0,
  currency: defaultCurrency || "BRL",
  billingType: "recurring",
  recurrence: "monthly",
  useWhatsapp: true,
  useFacebook: true,
  useInstagram: true,
  useCampaigns: true,
  useSchedules: true,
  useInternalChat: false,
  useExternalApi: true,
  useKanban: true,
  useOfficialAPI: false,
  useIntegrations: true,
  isPublic: true,
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required(i18n.t("plans.form.name") + " *"),
});

const PlanModal = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  initialValue,
  loading,
}) => {
  const classes = useStyles();
  const filterDropdownClasses = useFilterDropdownStyles();
  const {
    currency,
    updateCurrency,
    getAvailableCurrencies,
  } = useCurrencyContext();

  const defaultCurrency = currency?.code || "BRL";
  const [record, setRecord] = useState(buildEmpty(defaultCurrency));

  useEffect(() => {
    if (!open) return;
    const safe = initialValue || {};
    setRecord({
      ...buildEmpty(defaultCurrency),
      ...safe,
      currency: safe.currency || defaultCurrency,
    });
  }, [initialValue, open, defaultCurrency]);

  const handleSubmit = async (data) => {
    if (data.currency && data.currency !== currency?.code) {
      try {
        await updateCurrency(data.currency);
      } catch (err) {
        console.error("Error persisting currency:", err);
      }
    }
    onSubmit(data);
  };

  const isEditing = !!initialValue?.id;
  const canDelete = isEditing && initialValue?.id !== 1;

  const dropdownMenuProps = {
    PaperProps: { className: filterDropdownClasses.paper },
    getContentAnchorEl: null,
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };

  const autocompletePaperClass = `${filterDropdownClasses.paper} ${classes.scrollableMenuPaper}`;

  const setAllToggles = (setFieldValue, value) => {
    FEATURE_TOGGLES.forEach((t) => setFieldValue(t.name, value));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>
          {isEditing
            ? i18n.t("plans.modal.editTitle")
            : i18n.t("plans.modal.createTitle")}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          className={classes.closeButton}
        >
          <LucideX size={18} strokeWidth={2.5} />
        </IconButton>
      </div>

      <Formik
        enableReinitialize
        initialValues={record}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values);
          setSubmitting(false);
        }}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent dividers className={classes.content}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Field
                    as={TextField}
                    label={i18n.t("plans.form.name")}
                    name="name"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    required
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Field
                    as={TextField}
                    label={i18n.t("plans.form.users")}
                    name="users"
                    type="number"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Field
                    as={TextField}
                    label={i18n.t("plans.form.connections")}
                    name="connections"
                    type="number"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Field
                    as={TextField}
                    label={i18n.t("settings.managers.plans.queues")}
                    name="queues"
                    type="number"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Field
                    as={TextField}
                    label={i18n.t("settings.managers.plans.value")}
                    name="amount"
                    variant="outlined"
                    size="small"
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={getAvailableCurrencies()}
                    getOptionLabel={(opt) =>
                      opt ? `${opt.symbol} ${opt.code}` : ""
                    }
                    getOptionSelected={(opt, val) => opt.code === val?.code}
                    filterOptions={currencyFilterOptions}
                    value={
                      getAvailableCurrencies().find(
                        (c) => c.code === values.currency
                      ) || null
                    }
                    onChange={(_, opt) =>
                      setFieldValue(
                        "currency",
                        opt?.code || defaultCurrency
                      )
                    }
                    classes={{ paper: autocompletePaperClass }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={i18n.t("settings.currency")}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.field}
                  >
                    <InputLabel>{i18n.t("plans.modal.billingType")}</InputLabel>
                    <Field
                      as={Select}
                      label={i18n.t("plans.modal.billingType")}
                      name="billingType"
                      MenuProps={dropdownMenuProps}
                    >
                      <MenuItem value="single">
                        {i18n.t("plans.modal.billingTypes.single")}
                      </MenuItem>
                      <MenuItem value="recurring">
                        {i18n.t("plans.modal.billingTypes.recurring")}
                      </MenuItem>
                      <MenuItem value="free">
                        {i18n.t("plans.modal.billingTypes.free")}
                      </MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                {values.billingType === "recurring" && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl
                      variant="outlined"
                      size="small"
                      className={classes.field}
                    >
                      <InputLabel>
                        {i18n.t("plans.modal.recurrence")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("plans.modal.recurrence")}
                        name="recurrence"
                        MenuProps={dropdownMenuProps}
                      >
                        <MenuItem value="weekly">
                          {i18n.t("plans.modal.recurrences.weekly")}
                        </MenuItem>
                        <MenuItem value="biweekly">
                          {i18n.t("plans.modal.recurrences.biweekly")}
                        </MenuItem>
                        <MenuItem value="monthly">
                          {i18n.t("plans.modal.recurrences.monthly")}
                        </MenuItem>
                        <MenuItem value="quarterly">
                          {i18n.t("plans.modal.recurrences.quarterly")}
                        </MenuItem>
                        <MenuItem value="semiannual">
                          {i18n.t("plans.modal.recurrences.semiannual")}
                        </MenuItem>
                        <MenuItem value="annual">
                          {i18n.t("plans.modal.recurrences.annual")}
                        </MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <div className={classes.sectionRow}>
                    <Typography className={classes.sectionLabel}>
                      {i18n.t("plans.modal.resources")}
                    </Typography>
                    <div className={classes.bulkActions}>
                      <button
                        type="button"
                        className={classes.bulkBtn}
                        onClick={() => setAllToggles(setFieldValue, true)}
                      >
                        <CheckSquare size={14} strokeWidth={1.8} />
                        {i18n.t("plans.modal.markAll")}
                      </button>
                      <button
                        type="button"
                        className={classes.bulkBtn}
                        onClick={() => setAllToggles(setFieldValue, false)}
                      >
                        <Square size={14} strokeWidth={1.8} />
                        {i18n.t("plans.modal.unmarkAll")}
                      </button>
                    </div>
                  </div>
                </Grid>

                {FEATURE_TOGGLES.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.name}>
                    <Field name={item.name}>
                      {({ field, form }) => (
                        <div className={classes.toggleItem}>
                          <span className={classes.toggleLabel}>
                            {i18n.t(item.labelKey)}
                          </span>
                          <CustomToggleSwitch
                            checked={!!field.value}
                            onChange={(e) =>
                              form.setFieldValue(item.name, e.target.checked)
                            }
                          />
                        </div>
                      )}
                    </Field>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>

            <ChatiaDialogActions
              loading={loading || isSubmitting}
              disabled={loading || isSubmitting}
              saveText={i18n.t("plans.form.save")}
              type="submit"
            >
              {canDelete && (
                <button
                  type="button"
                  className={classes.deleteBtn}
                  onClick={() => onDelete && onDelete()}
                  disabled={loading || isSubmitting}
                  style={{ marginRight: "auto" }}
                >
                  {i18n.t("plans.form.delete")}
                </button>
              )}
              <CancelButton onClick={onClose}>
                {i18n.t("plans.modal.cancel")}
              </CancelButton>
            </ChatiaDialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default PlanModal;
